import { type FormikErrors } from "formik";
import { BTServiceFileUpload } from "legacyComponents/FileUploadContainer.types";
import { useCallback, useEffect, useState } from "react";
import { type RouteComponentProps } from "react-router";

import { BTPopover } from "@btnet/core-components/BTPopover";
import type { CalendarFeedItemViewModel } from "@btnet/services-btnet/handlers/models/calendarFeedItemViewModel";

import {
    BdsActionBar,
    BdsButton,
    BdsButtonGroup,
    BdsIconPencilSimple,
    BdsRollupButton,
} from "@buildertrend/components";

import { type BTSelectItem } from "types/apiResponse/apiResponse";
import { BTLoginTypes, InternalUserActivationStatus, JobStatusTypes } from "types/enum";

import { useEditMode } from "utilities/editMode/useEditMode";
import { getSelectedValue, getSelectedValues } from "utilities/form/form";
import yup from "utilities/form/yup";
import { routes } from "utilities/routes";

import { BTAlert } from "commonComponents/btWrappers/BTAlert/BTAlert";
import { btConfirm } from "commonComponents/btWrappers/BTConfirm/BTConfirm";
import { BTForm } from "commonComponents/btWrappers/BTForm/BTForm";
import { BTLayoutContent } from "commonComponents/btWrappers/BTLayout/BTLayout";
import { type IModalConfiguration } from "commonComponents/btWrappers/BTModal/BTModal";
import { BTModalLayout } from "commonComponents/btWrappers/BTModal/BTModalLayout";
import { BTRow } from "commonComponents/btWrappers/BTRow/BTRow";
import { BTTabs } from "commonComponents/btWrappers/BTTabs/BTTabs";
import { BTUserInfoDisplay } from "commonComponents/btWrappers/BTUser/BTUserInfoDisplay";
import { LinkingAction } from "commonComponents/entity/accounting/AccountingLinkingOptions/AccountingLinkingOptions.api.types";
import {
    type UnlinkAccountingResponse,
    type UpdateAccountingResponse,
} from "commonComponents/entity/accounting/LinkedAccountingEntity/LinkedAccountingEntity.api.types";
import { cellEmailLookupValidator } from "commonComponents/entity/cellEmailLookup/cellEmailLookup.utils";
import { VerificationMessageType } from "commonComponents/entity/cellPhone/CellPhoneInput/CellPhoneInput.types";
import {
    cellPhoneValidator,
    getCellPhoneFieldValue,
} from "commonComponents/entity/cellPhone/CellPhoneInput/CellPhoneInput.utils";
import {
    CustomFieldsFormValuesValidator,
    PropsToValueMapping,
} from "commonComponents/entity/customField/CustomFieldContainer/CustomFieldContainer";
import { Email } from "commonComponents/entity/email/InputEmail/InputEmail.types";
import { InputEmailValidator } from "commonComponents/entity/email/InputEmail/InputEmail.utils";
import { InputEmailListValidator } from "commonComponents/entity/email/InputEmailList/InputEmailList.utils";
import {
    type IJobAccess,
    type JobAccessEntity,
} from "commonComponents/entity/jobAccess/JobAccess/JobAccess.api.types";
import {
    getAllJobStatuses,
    mapJobStatusFilterTypesToJobStatusType,
} from "commonComponents/entity/jobAccess/JobAccess/JobAccess.utils";
import { NotificationPreferencesTableValidators } from "commonComponents/entity/notificationPreferences/NotificationPreferencesTable";
import {
    mapPreferencesToRowsExpandable,
    setRoleViewPermissions,
} from "commonComponents/entity/notificationPreferences/NotificationPreferencesTableExpandable";
import PermissionWizard from "commonComponents/entity/permissionWizard/PermissionWizard/PermissionWizard";
import {
    getUnsavedChangesModalConfig,
    showUnsavedChangesPrompt,
} from "commonComponents/utilities/DirtyTracking/UnsavedChangesPrompt";
import {
    type IBTInjectedFormikProps,
    withFormikBT,
} from "commonComponents/utilities/Formik/WithFormikBT";
import { TextVerificationBanner } from "commonComponents/utilities/HeaderAlerter/TextVerificationBanner/TextVerificationBanner";
import { HotkeyCommands } from "commonComponents/utilities/Hotkey/hotkey.utility";
import { PageSection } from "commonComponents/utilities/PageSection/PageSection";
import { FormikValidationSummary } from "commonComponents/utilities/validationSummary/FormikValidationSummary/FormikValidationSummary";

import { type IFilterHandler } from "entity/filters/Filter/Filter.api.handler";
import InternalUserDeleteConflicts from "entity/internalUser/InternalUserDeleteConflicts/InternalUserDeleteConflicts";
import {
    type IInternalUserDeleteConflictsFormValues,
    type InternalUserDeleteConflictsEntity,
} from "entity/internalUser/InternalUserDeleteConflicts/InternalUserDeleteConflicts.api.types";
import { type IInternalUserHandler } from "entity/internalUser/InternalUserDetails/InternalUser.api.handler";
import OrgOwnerRoleConfirm from "entity/internalUser/InternalUserDetails/OrgOwnerRoleConfirm";
import { AccountingTab } from "entity/internalUser/InternalUserDetails/tabs/AccountingTab";
import { InternalUserMessaging } from "entity/internalUser/InternalUserDetails/tabs/InternalUserMessaging";
import { InternalUserPayrollTab } from "entity/internalUser/InternalUserDetails/tabs/InternalUserPayrollTab";
import { InternalUsersSecurityLoginTab } from "entity/internalUser/InternalUserDetails/tabs/InternalUsersSecurityLoginTab";
import InternalUserTakeoffTab from "entity/internalUser/InternalUserDetails/tabs/InternalUserTakeoffTab";
import InternalUserVendorTab from "entity/internalUser/InternalUserDetails/tabs/InternalUserVendorTab";
import { InternalUserJobAccess } from "entity/internalUser/InternalUserDetails/tabs/JobAccessTab";
import { InternalUserNotifications } from "entity/internalUser/InternalUserDetails/tabs/Notifications";
import { InternalUserOther } from "entity/internalUser/InternalUserDetails/tabs/OtherTab";
import { InternalUserOverview } from "entity/internalUser/InternalUserDetails/tabs/OverviewTab";
import { InternalUserPermissions } from "entity/internalUser/InternalUserDetails/tabs/PermissionsTab";
import MultiFactorAuthUserStatus from "entity/internalUser/MultiFactorAuthUserStatus/MultiFactorAuthUserStatus";
import {
    type Role,
    roleValidationSchema,
} from "entity/internalUser/RolePermissions/RolePermissions.api.types";
import { RouteSubscriptionManager } from "entity/SubscriptionManager/SubscriptionManagerRoute";
import { type TakeoffSettingsFormActions } from "entity/takeoff/TakeoffSettings/TakeoffSettings.api.types";
import { type HomeDepotSettingsFormActions } from "entity/vendor/HomeDepotSettings/HomeDepotSettings.api.types";

import "./InternalUserDetails.less";

import {
    blankSignatureImage,
    FeedType,
    type IInternalUserFormValues,
    type InternalUserEntity,
    type InternalUserFormActions,
    InternalUserTabKeys,
    type IRoleExtraData,
    unselectedRoleDropdownId,
} from "./InternalUser.api.types";

interface IUrlProps {}

interface IInternalUserProps extends RouteComponentProps<IUrlProps> {
    entity: InternalUserEntity;
    actionBeingPerformed: InternalUserFormActions;
    handler?: IInternalUserHandler;
    filterHandler?: IFilterHandler;
    initialTab?: InternalUserTabKeys;
    lastActionSaveAndNew: boolean;
    onSubmit: (
        values: IInternalUserFormValues,
        action: InternalUserFormActions,
        setErrors: (errors: FormikErrors<IInternalUserFormValues>) => void,
    ) => Promise<boolean>;
    onSaveAndNew: (
        newValues: IInternalUserFormValues,
        setErrors: (errors: FormikErrors<IInternalUserFormValues>) => void,
    ) => Promise<boolean>;
    onAddUpdateCalendarFeed: (
        id: number,
        feedType: FeedType,
        options: number[],
        hasFeed: boolean,
        setFieldValue: (field: string, value: any) => void,
        feedId?: number,
    ) => void;
    onDeleteCalendarFeed: (
        id: number,
        feedType: FeedType,
        setFieldValue: (field: string, value: any) => void,
        feedId?: number,
    ) => void;
    onDelete: () => Promise<void>;
    onReloadUser: () => Promise<void>;
    onUpdateAccounting: (id: number) => Promise<UpdateAccountingResponse>;
    onUnlinkAccounting: (id: number) => Promise<UnlinkAccountingResponse>;
    onLinkEntityToAccounting: (selectedAccountingEmployee: string) => Promise<void>;
    onReload: () => Promise<void>;
    onInitiateHomeDepotConnection: () => void;
    onDisconnectHomeDepot: () => Promise<void>;
    onInitiatePartnerAccountConnection: () => void;
    onDisconnectPartnerAccount: () => Promise<void>;
    deleteConflicts?: InternalUserDeleteConflictsEntity;
    onCloseDeleteConflicts: () => void;
    onReassignJobsitesForUserDeletion: (newLoginId: number) => Promise<void>;
    onReassignItemsForUserDeletion: (
        formValues: IInternalUserDeleteConflictsFormValues,
    ) => Promise<void>;
    onJobAccessChanged: (jobAccess: IJobAccess) => void;
    onToggleAllJobAccess: (jobAccess: IJobAccess[], checked: boolean) => void;
    jobAccess: JobAccessEntity | null;
    permissionWizardUserId: number;
    jobAccessIds: number[];
    shouldRenderAddUserToJobModal: boolean;
    onCloseAddUsersToJobModal: () => void;
    onDoNotUpdateUserPermissions: () => void;
    modalConfig?: IModalConfiguration;
    getRoles: () => Promise<{
        roleTreeData: BTSelectItem<IRoleExtraData>[];
        roles: Role[];
    }>;
    shouldRenderTextVerificationAlert: boolean;
    initialEditMode?: boolean;
}

function InternalUserInternal(
    props: IBTInjectedFormikProps<IInternalUserProps, IInternalUserFormValues>,
) {
    const { isInEditMode, enterEditMode, exitEditMode } = useEditMode();

    const {
        entity,
        handler,
        actionBeingPerformed,
        onCloseDeleteConflicts,
        values,
        errors,
        match,
        initialTab,
        submitCount,
        onReassignItemsForUserDeletion,
        onReassignJobsitesForUserDeletion,
        onSubmit,
        getDirtyTrackingProps,
        onSaveAndNew,
        onDelete,
        onReloadUser,
        onInitiateHomeDepotConnection,
        setFieldValue,
        onDisconnectHomeDepot,
        onInitiatePartnerAccountConnection,
        onDisconnectPartnerAccount,
        deleteConflicts,
        onJobAccessChanged,
        onToggleAllJobAccess,
        setSubmitting,
        resetForm,
        handleSubmit,
        validateForm,
        setErrors,
        permissionWizardUserId,
        jobAccessIds,
        shouldRenderAddUserToJobModal,
        onCloseAddUsersToJobModal,
        onDoNotUpdateUserPermissions,
        modalConfig,
        getRoles,
        shouldRenderTextVerificationAlert,
        setValues,
        initialEditMode,
    } = props;

    useEffect(() => {
        if (initialEditMode) {
            enterEditMode();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const [activeKey, setActiveKey] = useState<InternalUserTabKeys>(
        initialTab ?? InternalUserTabKeys.Overview,
    );
    const [showOrgOwnerRoleConfirm, setShowOrgOwnerRoleConfirm] = useState(false);
    const [customSaveType, setCustomSaveType] = useState<InternalUserFormActions>();
    const showEditActions = ![
        InternalUserTabKeys.Accounting,
        InternalUserTabKeys.Payroll,
        InternalUserTabKeys.Vendors,
        InternalUserTabKeys.Takeoff,
    ].includes(activeKey);

    // Refresh roles on tab focus
    const refreshRolesOnTabFocus = useCallback(async () => {
        const hidden = document["hidden"];
        if (hidden !== undefined && !hidden) {
            const resp = await getRoles();
            if (resp.roles.length > 0 && resp.roleTreeData.length > 0) {
                const roleId = getSelectedValue(resp.roleTreeData);
                setValues({
                    ...values,
                    role: roleId ?? unselectedRoleDropdownId,
                    roles: resp.roles,
                    roleTreeData: resp.roleTreeData,
                });
            }
        }
    }, [getRoles, setValues, values]);

    useEffect(() => {
        document.addEventListener("visibilitychange", refreshRolesOnTabFocus, false);
        return () => {
            document.removeEventListener("visibilitychange", refreshRolesOnTabFocus);
        };
    }, [refreshRolesOnTabFocus]);

    const handleTabClick = useCallback((key: string) => {
        setActiveKey(key as InternalUserTabKeys);
    }, []);

    const handleInvite = useCallback(async () => {
        setFieldValue("sendInvitation", true);
    }, [setFieldValue]);

    const handleCancelInvite = useCallback(async () => {
        setFieldValue("sendInvitation", false);
    }, [setFieldValue]);

    const handleInviteClick = useCallback(() => {
        if (values.sendInvitation) {
            void handleCancelInvite();
        } else {
            void handleInvite();
        }
    }, [handleCancelInvite, handleInvite, values.sendInvitation]);

    const handleCustomSave = useCallback(
        async (action: InternalUserFormActions = "update") => {
            let actionConfirmed = false;
            setSubmitting(true);
            handleSubmit();
            const formErrors = await validateForm();
            if (Object.keys(formErrors).length === 0) {
                if (action === "saveAndNew") {
                    actionConfirmed = await onSaveAndNew(values, setErrors);
                    if (actionConfirmed) {
                        resetForm();
                    }
                } else {
                    actionConfirmed = await onSubmit(values, action, setErrors);
                }
            }
            setSubmitting(false);
            if (action === "saveAndClose" && actionConfirmed) {
                modalConfig?.beforeClose();
            }
            if (action === "update" && actionConfirmed) {
                exitEditMode();
            }
            if (showOrgOwnerRoleConfirm) {
                setShowOrgOwnerRoleConfirm(false);
            }
        },
        [
            setSubmitting,
            handleSubmit,
            validateForm,
            onSaveAndNew,
            values,
            setErrors,
            resetForm,
            onSubmit,
            showOrgOwnerRoleConfirm,
            modalConfig,
            exitEditMode,
        ],
    );

    const handleSaveAction = useCallback(
        async (customSaveType: InternalUserFormActions) => {
            const { dirtyKeys } = getDirtyTrackingProps();
            const OrgOwnerRoleId = values.roles.find(
                (r) => r.name.toLowerCase() === "org owner" && r.isSystemDefault,
            )?.id;
            const showOrgOwnerRoleConfirm =
                OrgOwnerRoleId !== undefined &&
                dirtyKeys.includes("role") &&
                values.role === OrgOwnerRoleId &&
                entity.orgOwnerCount >= 2;
            if (showOrgOwnerRoleConfirm) {
                setShowOrgOwnerRoleConfirm(true);
                setCustomSaveType(customSaveType);
            } else {
                await handleCustomSave(customSaveType);
            }
        },
        [entity.orgOwnerCount, getDirtyTrackingProps, handleCustomSave, values.role, values.roles],
    );

    const handleSaveAndClose = useCallback(async () => {
        await handleSaveAction("saveAndClose");
    }, [handleSaveAction]);

    const handleSave = useCallback(async () => {
        await handleSaveAction(entity.internalUser.id === 0 ? "save" : "update");
    }, [entity.internalUser.id, handleSaveAction]);

    const handleSaveAndNew = useCallback(async () => {
        await handleSaveAction("saveAndNew");
    }, [handleSaveAction]);

    const handleDisconnectHomeDepot = useCallback(async () => {
        const { isOnlyConnectedUser } = entity.vendorAccountUserDetails!;
        const onlyUserDisconnectContent =
            "By disconnecting this user, this will disconnect Buildertrend from The Home Depot and will no longer sync information from past purchases and saved products.";
        const standardDisconnectContent =
            "By disconnecting, this user will no longer be able to send items to The Home Depot from Buildertrend.";
        btConfirm({
            title: "Disconnect from The Home Depot?",
            content: isOnlyConnectedUser ? onlyUserDisconnectContent : standardDisconnectContent,
            okText: "Disconnect",
            okType: "danger",
            onOk: async () => {
                await onDisconnectHomeDepot();
                handleTabClick(InternalUserTabKeys.Overview);
            },
        });
    }, [entity.vendorAccountUserDetails, onDisconnectHomeDepot, handleTabClick]);

    const handleDisconnectPartner = useCallback(async () => {
        const { internalUser, takeoffUserDetails } = entity;
        const name = internalUser.isSelf
            ? "You"
            : `${internalUser.firstName} ${internalUser.lastName}`;
        const possesive = internalUser.isSelf ? "your" : "their";
        const onlyUserDisconnectContent =
            "By disconnecting this user, this will disconnect Buildertrend from Buildertrend Takeoff. Internal users will no longer be able to launch or use Buildertrend Takeoff with their Buildertrend accounts.";
        const standardDisconnectContent = `${name} will no longer be able to launch or use Buildertrend Takeoff with ${possesive} Buildertrend account.`;
        btConfirm({
            title: "Disconnect from Buildertrend Takeoff?",
            content: takeoffUserDetails?.isOnlyConnectedUser
                ? onlyUserDisconnectContent
                : standardDisconnectContent,
            okText: "Disconnect",
            okType: "danger",
            onOk: async () => {
                await onDisconnectPartnerAccount();
                handleTabClick(InternalUserTabKeys.Overview);
            },
        });
    }, [entity, onDisconnectPartnerAccount, handleTabClick]);

    const handleJobAccessOnRoleChange = useCallback(
        (roleId: number, jobAccess?: JobAccessEntity) => {
            if (jobAccess === undefined) {
                jobAccess = values.jobAccess!;
            }
            if (roleId < 0) {
                return jobAccess;
            }
            const statuses = entity.internalUser.roles.find(
                (r) => r.id === roleId,
            )!.jobStatusAccess;
            jobAccess.jobs = jobAccess.jobs.map((job) => {
                const jobStatus = mapJobStatusFilterTypesToJobStatusType(job.jobStatus);
                if (jobStatus === JobStatusTypes.Template) {
                    return job;
                }
                if (!statuses.includes(jobStatus!)) {
                    job.hasAccess = false;
                    job.hasNotificationAccess = false;
                }
                return job;
            });
            return jobAccess;
        },
        [entity.internalUser.roles, values.jobAccess],
    );

    const handleRoleChange = useCallback(
        (field: string, value) => {
            const role = values.roles.find((r) => r.id === value);
            const notificationPrefs = setRoleViewPermissions(values.notificationPreferences, role!);
            if (values.jobAccess !== null) {
                const jobAccess = handleJobAccessOnRoleChange(value);
                setFieldValue("jobAccess", jobAccess);
            }
            setFieldValue("notificationPreferences", notificationPrefs);
            setFieldValue(field, value);
        },
        [
            handleJobAccessOnRoleChange,
            setFieldValue,
            values.jobAccess,
            values.notificationPreferences,
            values.roles,
        ],
    );

    const getModalConfig = useCallback(() => {
        return getUnsavedChangesModalConfig(
            getDirtyTrackingProps,
            {
                onCloseWithSave: handleSaveAndClose,
                onCloseWithoutSave: modalConfig?.beforeClose,
            },
            modalConfig?.parentRoute,
        );
    }, [
        getDirtyTrackingProps,
        handleSaveAndClose,
        modalConfig?.beforeClose,
        modalConfig?.parentRoute,
    ]);

    const handleCancel = useCallback(() => {
        const dirtyTrackingProps = getDirtyTrackingProps();
        if (dirtyTrackingProps.isDirty) {
            showUnsavedChangesPrompt(dirtyTrackingProps.dirtyKeys, {
                onCloseWithSave: async () => {
                    await handleCustomSave(entity.internalUser.id === 0 ? "save" : "update");
                },
                onCloseWithoutSave: () => {
                    resetForm();
                    exitEditMode();
                },
            });
        } else {
            if (entity.internalUser.id === 0) {
                modalConfig?.beforeClose();
            } else {
                exitEditMode();
            }
        }
    }, [
        entity.internalUser.id,
        exitEditMode,
        getDirtyTrackingProps,
        handleCustomSave,
        modalConfig,
        resetForm,
    ]);

    const isSelf = entity.internalUser.isSelf;
    const canView = entity.canView;
    const canAdd = entity.canAdd;
    const canEdit =
        (entity.canEdit && entity.internalUser.id !== 0) ||
        (entity.internalUser.id === 0 && canAdd);

    const visibleRows = values.notificationPreferences.filter(
        (row) => !row.isCategoryRow && !row.isGroupRow && row.canView,
    );
    const canViewNotifications = entity.canViewNotifications && visibleRows.length;
    const isNew = entity.internalUser.id === 0;
    const validAdd = isNew && canAdd;
    const validUpdate = !isNew && canEdit;
    const validModify = validAdd || validUpdate;
    const isActive = values.loginEnabled;
    const isSaveVisible = validModify || isSelf;
    const isSaveAddVisible =
        canAdd &&
        ((initialTab !== InternalUserTabKeys.Other &&
            initialTab !== InternalUserTabKeys.Notifications) ||
            !isSelf);
    const isDeleteVisible = entity.canDelete && entity.internalUser.id !== 0 && !isSelf;
    const routeProps: RouteComponentProps = {
        history: props.history,
        location: props.location,
        match: props.match,
    };

    const integrationTabProps = {
        mouseEnterDelay: 0.2,
        disabled: !isInEditMode,
        placement: "topLeft" as const,
        content: "Enter view mode to make changes to this tab",
    };

    const internalUserFullName = `${entity.internalUser.firstName} ${entity.internalUser.lastName}`;

    const shouldShowUserMessage =
        !isNew &&
        isActive &&
        !entity.internalUser.invitationDate &&
        !values.sendInvitation &&
        values.primaryEmail;
    const inviteUserMessage =
        "This user has not been invited. Invite this user to allow them to start using Buildertrend.";

    const isEmailRequired =
        values.loginEnabled &&
        (values.sendInvitation ||
            entity.internalUser.activationStatus ===
                InternalUserActivationStatus.ActiveInviteAccepted ||
            entity.internalUser.activationStatus ===
                InternalUserActivationStatus.ActiveInvitePending ||
            (entity.internalUser.accountConfirmationDate !== undefined &&
                entity.internalUser.accountConfirmationDate !== null));
    const title = isNew
        ? "Add Internal User"
        : `${entity.internalUser.firstName} ${entity.internalUser.lastName}`;

    let manageRolesLink = routes.internalUsers.getInternalUserRoleDetailsLink(values.role);
    if (values.role === -1) {
        manageRolesLink =
            routes.settings.settings + routes.internalUsers.getInternalUserSettingsLink();
    }

    let jobStatusAccess = getAllJobStatuses([]) as JobStatusTypes[];
    if (values.role > 0) {
        jobStatusAccess = entity.internalUser.roles.find(
            (r) => r.id === values.role,
        )!.jobStatusAccess;
    }

    const editExistingSaveActions = isSaveVisible && (
        <BdsButton
            id="InternalUserSaveExistingButtonId"
            displayType="primary"
            testid="save-edit-existing"
            text="Save"
            hotkey={HotkeyCommands.save.command}
            onClick={handleSave}
            loading={actionBeingPerformed === "update"}
            disabled={!!actionBeingPerformed}
            value="save"
        />
    );

    const createNewSaveActions = (
        <BdsButtonGroup
            id="InternalUserPresentationalSaveGroup"
            testid="internalUserPresentationalSaveGroup"
            menuTestid="internalUserPresentationalSaveGroupMenu"
            rollupTestid="internalUserPresentationalSaveGroupRollup"
            boundary=".ant-modal-body"
            splitButton
            flexAlign="end"
        >
            {isSaveVisible && (
                <BdsRollupButton
                    id="InternalUserSaveButtonId"
                    onClick={handleSave}
                    testid="save-edit"
                    text="Save"
                    displayType="primary"
                    loading={actionBeingPerformed === "save" || actionBeingPerformed === "update"}
                    disabled={!!actionBeingPerformed}
                    hotkey={HotkeyCommands.save.command}
                    value="save"
                    minStage={1}
                />
            )}
            <BdsRollupButton
                displayType="primary"
                id="relatedActionsButton"
                testid="relatedActionsButton"
                value="openRelatedActions"
                ariaLabel="Related actions"
            >
                {isSaveVisible && (
                    <BdsRollupButton
                        id="SaveAndClose"
                        testid="saveAndClose-edit"
                        text="Save and close"
                        loading={actionBeingPerformed === "saveAndClose"}
                        disabled={!!actionBeingPerformed}
                        hotkey={HotkeyCommands.saveAndClose.command}
                        onClick={handleSaveAndClose}
                        value="saveAndClose"
                    />
                )}
                {isSaveVisible && isSaveAddVisible && (
                    <BdsRollupButton
                        id="SaveAndNew"
                        testid="saveAndNew-edit"
                        text="Save and new"
                        loading={actionBeingPerformed === "saveAndNew"}
                        disabled={!!actionBeingPerformed}
                        onClick={handleSaveAndNew}
                        value="saveAndNew"
                    />
                )}
            </BdsRollupButton>
        </BdsButtonGroup>
    );

    const actions = isInEditMode ? (
        <BdsActionBar
            id="InternalUserPresentationalEditFooter"
            testid="InternalUserPresentationalEditFooter"
        >
            <BdsButtonGroup
                id="InternalUserPresentationalCancelGroup"
                testid="internalUserPresentationalCancelGroup"
                menuTestid="internalUserPresentationalCancelGroupMenu"
                rollupTestid="internalUserPresentationalCancelGroupRollup"
                grow
                flexAlign="end"
            >
                <BdsRollupButton
                    id="Cancel"
                    testid="cancel-edit"
                    text="Cancel"
                    displayType="secondary"
                    disabled={!!actionBeingPerformed}
                    onClick={handleCancel}
                    value="Cancel"
                />
            </BdsButtonGroup>
            {isNew ? createNewSaveActions : editExistingSaveActions}
        </BdsActionBar>
    ) : (
        showEditActions && (
            <BdsActionBar
                grow
                testid="InternalUserPresentationalViewFooter"
                className="InternalUserView"
            >
                <BdsButtonGroup
                    id="InternalUserPresentationalFooterActions"
                    className="InternalUserPresentationalFooterActions"
                    testid="internalUserPresentationalFooterActions"
                    menuTestid="internalUserPresentationalButtonGroupMenu"
                    rollupTestid="internalUserPresentationalButtonGroupRollup"
                    boundary=".ant-modal-body"
                >
                    {(canEdit || isSelf) && (
                        <BdsRollupButton
                            id="InternalUserEditButtonId"
                            onClick={() => enterEditMode()}
                            testid="edit"
                            icon={<BdsIconPencilSimple />}
                            displayType="secondary"
                            ariaLabel="Edit Internal User"
                            disabled={!!actionBeingPerformed}
                            value="edit"
                            minStage={1}
                        />
                    )}
                    {isDeleteVisible && (
                        <BdsRollupButton
                            id="InternalUserDeleteButtonId"
                            className="InternalUserDeleteButton"
                            onClick={onDelete}
                            testid="delete-rollup"
                            text="Delete"
                            displayType="danger"
                            loading={actionBeingPerformed === "delete"}
                            disabled={!!actionBeingPerformed}
                            hotkey={HotkeyCommands.delete.command}
                            value="delete"
                            maxStage={0}
                        />
                    )}
                </BdsButtonGroup>
            </BdsActionBar>
        )
    );

    return (
        <>
            {deleteConflicts && (
                <InternalUserDeleteConflicts
                    loginId={entity.internalUser.id}
                    internalUserName={internalUserFullName}
                    entity={deleteConflicts}
                    onReassignJobsitesForUserDeletion={onReassignJobsitesForUserDeletion}
                    onReassignItemsForUserDeletion={onReassignItemsForUserDeletion}
                    modalConfig={{
                        parentRoute: routeProps.match.url,
                        beforeClose: onCloseDeleteConflicts,
                    }}
                    {...routeProps}
                />
            )}
            <BTModalLayout
                title={modalConfig ? title : undefined}
                footerContent={actions}
                modalConfig={getModalConfig()}
                className="InternalUserModalLayout"
            >
                <BTForm>
                    <BTLayoutContent>
                        {shouldRenderAddUserToJobModal && (
                            <PermissionWizard
                                jobIds={jobAccessIds}
                                internalUserIds={[permissionWizardUserId]}
                                onComplete={onCloseAddUsersToJobModal}
                                onCancel={onDoNotUpdateUserPermissions}
                                modalConfig={{
                                    parentRoute: match.url,
                                    beforeClose: onDoNotUpdateUserPermissions,
                                }}
                                useBds
                            />
                        )}
                        {shouldShowUserMessage && (
                            <BTAlert
                                type="info"
                                message={inviteUserMessage}
                                data-testid="invitedUserMessage"
                                showIcon
                                closable
                            />
                        )}
                        {shouldRenderTextVerificationAlert && (
                            <TextVerificationBanner
                                handlePreferencesClicked={() =>
                                    handleTabClick(InternalUserTabKeys.Notifications)
                                }
                            />
                        )}
                        <FormikValidationSummary
                            errors={errors}
                            values={values}
                            showAfterSubmit={submitCount}
                            scheme={InternalUserValidators(props)}
                        />
                        {entity.internalUser.id !== 0 && (
                            <PageSection>
                                <BTRow gutter={8}>
                                    <BTUserInfoDisplay
                                        globalUserId={entity.internalUser.id}
                                        displayName={internalUserFullName}
                                        showManageUser={false}
                                        userProfile={{
                                            name: internalUserFullName,
                                            userType: BTLoginTypes.BUILDER,
                                            title: "",
                                            email:
                                                entity.internalUser.primaryEmail?.fullEmail ?? "",
                                            phone: entity.internalUser.phone,
                                            cellPhone: entity.internalUser.cell,
                                            primaryContact: "",
                                            canStartChatConversation:
                                                entity.canStartChatConversation,
                                        }}
                                        hideAvatar={true}
                                    />
                                </BTRow>
                                <MultiFactorAuthUserStatus isMfaEnabled={entity.isMfaEnabled} />
                            </PageSection>
                        )}
                        <BTTabs
                            data-testid="internal-user-tabs"
                            activeKey={activeKey}
                            onTabClick={handleTabClick}
                            className="InternalUserTabs"
                        >
                            <BTTabs.TabPane tab="Overview" key={InternalUserTabKeys.Overview}>
                                <InternalUserOverview
                                    internalUserDetails={entity}
                                    canEdit={canEdit}
                                    onChangeActiveKey={handleTabClick}
                                    onRoleChange={handleRoleChange}
                                    onInviteClick={handleInviteClick}
                                    isEmailRequired={isEmailRequired}
                                    manageRolesLink={manageRolesLink}
                                    inEditMode={isInEditMode}
                                    {...props}
                                />
                            </BTTabs.TabPane>
                            {canView && (
                                <BTTabs.TabPane
                                    tab="Permissions"
                                    key={InternalUserTabKeys.Permissions}
                                >
                                    <InternalUserPermissions
                                        internalUserDetails={entity}
                                        canEdit={canEdit}
                                        onChangeActiveKey={handleTabClick}
                                        onRoleChange={handleRoleChange}
                                        isMfaEnabled={entity.isMfaEnabled}
                                        manageRolesLink={manageRolesLink}
                                        inEditMode={isInEditMode}
                                        {...props}
                                    />
                                </BTTabs.TabPane>
                            )}
                            {canViewNotifications && (
                                <BTTabs.TabPane
                                    tab="Notifications"
                                    key={InternalUserTabKeys.Notifications}
                                >
                                    <InternalUserNotifications
                                        internalUserDetails={entity}
                                        onChangeActiveKey={handleTabClick}
                                        canEdit={canEdit}
                                        isActive={isActive}
                                        inEditMode={isInEditMode}
                                        {...props}
                                    />
                                </BTTabs.TabPane>
                            )}
                            <BTTabs.TabPane tab="Messaging" key={InternalUserTabKeys.Messaging}>
                                <InternalUserMessaging
                                    {...props}
                                    internalUserEntity={entity}
                                    isSelf={isSelf}
                                    canEdit={isSaveVisible}
                                    inEditMode={isInEditMode}
                                />
                            </BTTabs.TabPane>

                            <BTTabs.TabPane
                                tab={`Job Access (${entity.internalUser.jobAccessCount})`}
                                key={InternalUserTabKeys.JobAccess}
                            >
                                <InternalUserJobAccess
                                    {...props}
                                    userId={entity.internalUser.id}
                                    handler={handler}
                                    onJobAccessChanged={onJobAccessChanged}
                                    onToggleAllJobAccess={onToggleAllJobAccess}
                                    disabled={!canEdit || !entity.canEditJobAccess || !isInEditMode}
                                    userIsArchived={
                                        entity.internalUser.activationStatus ===
                                        InternalUserActivationStatus.Archived
                                    }
                                    inEditMode={isInEditMode}
                                    isSelf={isSelf}
                                    jobGroups={entity.jobGroups}
                                    userName={entity.internalUser.firstName}
                                    onJobAccessOnRoleChange={handleJobAccessOnRoleChange}
                                    roleJobStatusAccess={jobStatusAccess}
                                />
                            </BTTabs.TabPane>
                            <BTTabs.TabPane
                                tab="Security & Login"
                                key={InternalUserTabKeys.SecurityLogins}
                            >
                                <InternalUsersSecurityLoginTab
                                    {...props}
                                    internalUserEntity={entity}
                                    isSelf={isSelf}
                                    canEdit={canEdit}
                                    onSubmit={handleSave}
                                    inEditMode={isInEditMode}
                                />
                            </BTTabs.TabPane>
                            <BTTabs.TabPane tab="Other" key={InternalUserTabKeys.Other}>
                                <InternalUserOther
                                    {...props}
                                    internalUserEntity={entity}
                                    isSelf={isSelf}
                                    canEdit={canEdit}
                                    onSubmit={handleSave}
                                    inEditMode={isInEditMode}
                                />
                            </BTTabs.TabPane>
                            {canView && entity.accountingEntity && (
                                <BTTabs.TabPane
                                    tab={
                                        <BTPopover
                                            data-testid="accounting-tooltip"
                                            {...integrationTabProps}
                                        >
                                            <span>Accounting</span>
                                        </BTPopover>
                                    }
                                    key={InternalUserTabKeys.Accounting}
                                >
                                    {isInEditMode ? (
                                        <PageSection
                                            title="Accounting"
                                            data-testid="accounting-tab-unavailable"
                                        >
                                            Enter view mode to make changes to this tab.
                                        </PageSection>
                                    ) : (
                                        <AccountingTab
                                            {...props}
                                            entity={entity}
                                            onReloadUser={onReloadUser}
                                        />
                                    )}
                                </BTTabs.TabPane>
                            )}
                            {entity.payrollSettingsEntity &&
                                entity.payrollSettingsEntity.canView && (
                                    <BTTabs.TabPane
                                        tab={
                                            <BTPopover
                                                data-testid="payroll-tooltip"
                                                {...integrationTabProps}
                                            >
                                                <span>Payroll</span>
                                            </BTPopover>
                                        }
                                        key={InternalUserTabKeys.Payroll}
                                    >
                                        {isInEditMode ? (
                                            <PageSection
                                                title="Payroll"
                                                data-testid="payroll-tab-unavailable"
                                            >
                                                Enter view mode to make changes to this tab.
                                            </PageSection>
                                        ) : (
                                            <InternalUserPayrollTab
                                                {...props}
                                                entity={entity.payrollSettingsEntity}
                                                id={entity.internalUser.id}
                                                firstName={entity.internalUser.firstName}
                                            />
                                        )}
                                    </BTTabs.TabPane>
                                )}
                            {entity.vendorAccountUserDetails?.isBuilderConnected && (
                                <BTTabs.TabPane
                                    tab={
                                        <BTPopover
                                            data-testid="thd-tooltip"
                                            {...integrationTabProps}
                                        >
                                            <span>The Home Depot</span>
                                        </BTPopover>
                                    }
                                    key={InternalUserTabKeys.Vendors}
                                >
                                    {isInEditMode ? (
                                        <PageSection
                                            title="The Home Depot"
                                            data-testid="vendors-tab-unavailable"
                                        >
                                            Enter view mode to make changes to this tab.
                                        </PageSection>
                                    ) : (
                                        <InternalUserVendorTab
                                            vendorAccountUserDetails={
                                                entity.vendorAccountUserDetails
                                            }
                                            actionBeingPerformed={
                                                actionBeingPerformed as HomeDepotSettingsFormActions
                                            }
                                            onInitiateConnection={onInitiateHomeDepotConnection}
                                            onDisconnect={handleDisconnectHomeDepot}
                                            firstName={entity.internalUser.firstName}
                                            lastName={entity.internalUser.lastName}
                                        />
                                    )}
                                </BTTabs.TabPane>
                            )}
                            {entity.takeoffUserDetails?.isBuilderConnected && (
                                <BTTabs.TabPane
                                    tab={
                                        <BTPopover
                                            data-testid="takeoff-tooltip"
                                            {...integrationTabProps}
                                        >
                                            <span>Buildertrend Takeoff</span>
                                        </BTPopover>
                                    }
                                    key={InternalUserTabKeys.Takeoff}
                                >
                                    {isInEditMode ? (
                                        <PageSection
                                            title="Buildertrend Takeoff"
                                            data-testid="takeoff-tab-unavailable"
                                        >
                                            Enter view mode to make changes to this tab.
                                        </PageSection>
                                    ) : (
                                        <InternalUserTakeoffTab
                                            takeoffUserDetails={entity.takeoffUserDetails}
                                            actionBeingPerformed={
                                                actionBeingPerformed as TakeoffSettingsFormActions
                                            }
                                            onInitiateConnection={
                                                onInitiatePartnerAccountConnection
                                            }
                                            onDisconnect={handleDisconnectPartner}
                                            firstName={entity.internalUser.firstName}
                                            lastName={entity.internalUser.lastName}
                                        />
                                    )}
                                </BTTabs.TabPane>
                            )}
                        </BTTabs>
                    </BTLayoutContent>
                    <OrgOwnerRoleConfirm
                        visible={showOrgOwnerRoleConfirm}
                        onConfirm={() => handleCustomSave(customSaveType)}
                        onCancel={() => {
                            setShowOrgOwnerRoleConfirm(false);
                        }}
                        userName={values.firstName + " " + values.lastName}
                        actionBeingPerformed={actionBeingPerformed}
                    />
                    <RouteSubscriptionManager
                        modalConfig={{
                            parentRoute: match.url,
                            beforeClose: () => {
                                window.location.assign(window.location.href);
                            },
                        }}
                    />
                </BTForm>
            </BTModalLayout>
        </>
    );
}

const InternalUserValidators = (props: IInternalUserProps) =>
    yup.object().shape<IInternalUserFormValues>({
        firstName: yup.string().max(24).required().label("First Name"),
        lastName: yup.string().max(24).required().label("Last Name"),
        phone: yup.string().max(25).label("Phone Number").nullable(),
        cell: cellEmailLookupValidator,
        userEnabled: yup.bool(),
        role: yup.number().notOneOf([-1], "Required").label("Role").required(),
        roles: yup.array().of(roleValidationSchema),
        loginEnabled: yup.boolean().label("User Status"),
        addToAllExistingJobs: yup.boolean(),
        userAccessNewJobs: yup.boolean().required(),
        sendInvitation: yup.boolean().required(),
        notificationPreferences: NotificationPreferencesTableValidators,
        jobAccess: yup
            .object()
            .shape<JobAccessEntity>({
                jobs: yup
                    .array()
                    .label("Jobs")
                    .of(
                        yup.object().shape<IJobAccess>({
                            hasAccess: yup.bool().label("Has Access"),
                            jobId: yup.number().label("Job Id"),
                            jobStatus: yup.mixed().label("Job Status"),
                            jobName: yup.string().label("Job Name"),
                            groupNames: yup.array<string>().nullable().label("Group Names"),
                            dateCreated: yup.mixed().label("Date Opened"),
                        }),
                    ),
            })
            .nullable()
            .label("Job Access"),
        accountingEntityLinkingType: yup.number().label("Accounting Jobsite Linking Type"),
        createNewAccountingEntityOption: yup.number().label("Accounting Connection"),
        username: yup.string().label("Username"),
        enableConflictAlert: yup.bool(),
        conflictAlertMaxCount: yup
            .number()
            .when("enableConflictAlert", { is: true, then: yup.number().min(2).max(100) }),
        scheduleItems: yup.array<number>().label("Schedule Items"),
        scheduleItemsFeed: yup.object().shape<CalendarFeedItemViewModel>({
            feedId: yup.number(),
            feedType: yup.number(),
            feedUrl: yup.string(),
            feedOptions: yup.array(),
        }),
        leadActivities: yup.array<number>().label("Lead Activities"),
        leadActivitesFeed: yup.object().shape<CalendarFeedItemViewModel>({
            feedId: yup.number(),
            feedType: yup.number(),
            feedUrl: yup.string(),
            feedOptions: yup.array(),
        }),
        todos: yup.array<number>().label("To-Do's"),
        todosFeed: yup.object().shape<CalendarFeedItemViewModel>({
            feedId: yup.number(),
            feedType: yup.number(),
            feedUrl: yup.string(),
            feedOptions: yup.array(),
        }),
        geofencing: yup.bool(),
        defaultCostCodeId: yup.number().label("Default labor cost code"),
        defaultLaborCost: yup.number().label("Default labor cost"),
        defaultBillRate: yup.number().nullable().label("Default billable rate"),
        messageSignature: yup.string().nullable().label("Message Signature"),
        messageSignatureImage: yup.string().nullable().label("Message Signature Image"),
        signatureImageTempId: yup.number().nullable().label("Signature Image Temp Id"),
        signatureImage: yup.string().label("Message Signature Image"),
        tempFile: yup.mixed(),
        contactInfo: yup.string().max(150).label("Contact Information"),
        customFields: props.entity.canEdit ? CustomFieldsFormValuesValidator : yup.mixed(),
        payrollEmployeeId: yup.string().nullable(),
        payrollJobID: yup.string().nullable().label("Selected Payroll Job"),
        defaultPayrollItem: yup.string().nullable().label("Default Payroll Item"),
        isOutOfOffice: yup.bool(),
        primaryEmail: InputEmailValidator.nullable()
            .label("Primary Email")
            .when("sendInvitation", (sendInvitation, schema) => {
                if (sendInvitation) {
                    return InputEmailValidator.nullable().map({
                        emailAddress: (schema) =>
                            schema
                                .required("Email address is required for invitations.")
                                .label("Primary Email"),
                        friendlyName: (schema) => schema.label("Primary Email Label"),
                    });
                }
                return schema;
            })
            .when("loginEnabled", (loginEnabled, schema) => {
                if (
                    loginEnabled &&
                    (props.entity.internalUser.username !== "" ||
                        props.entity.internalUser.activationStatus ===
                            InternalUserActivationStatus.ActiveInviteAccepted)
                ) {
                    return InputEmailValidator.nullable().map({
                        emailAddress: (schema) =>
                            schema
                                .required("Email Address is required for active accounts.")
                                .label("Primary Email"),
                        friendlyName: (schema) => schema.label("Primary Email Label"),
                    });
                }
                return schema;
            })
            .when("loginEnabled", (loginEnabled, schema) => {
                if (
                    loginEnabled &&
                    props.entity.internalUser.activationStatus ===
                        InternalUserActivationStatus.ActiveInvitePending
                ) {
                    return InputEmailValidator.nullable().map({
                        emailAddress: (schema) =>
                            schema
                                .required("Email address is required for invitations.")
                                .label("Primary Email"),
                        friendlyName: (schema) => schema.label("Primary Email Label"),
                    });
                }
                return schema;
            }),
        additionalEmails: InputEmailListValidator.of(
            InputEmailValidator.map({
                emailAddress: (schema) => schema.required().label("Additional Email"),
                friendlyName: (schema) => schema.label("Additional Email Label"),
            }),
        ).max(6),
        roleTreeData: yup.mixed(),
        phoneCell: cellPhoneValidator,
        smsOptInStatus: yup.number(),
        verificationRequest: yup.number(),
    });

export const InternalUserPresentational = withFormikBT<IInternalUserProps, IInternalUserFormValues>(
    {
        // use the default values we specified above
        mapPropsToValues: (props: IInternalUserProps) => {
            const { entity } = props;
            const { internalUser } = entity;
            const rolesId = getSelectedValue(internalUser.role);

            const mapPreferences = () => {
                return entity.notificationPreferences
                    ? mapPreferencesToRowsExpandable(
                          entity.notificationPreferences,
                          internalUser.roles.find((r) => r.id === rolesId),
                      )
                    : [];
            };

            return {
                firstName: internalUser.firstName,
                lastName: internalUser.lastName,
                primaryEmail: internalUser.primaryEmail ?? new Email(undefined, "1"),
                additionalEmails: internalUser.additionalEmails,
                cell: internalUser.cell,
                phone: internalUser.phone,
                role: rolesId,
                loginEnabled: internalUser.loginEnabled,
                userAccessNewJobs: internalUser.autoAddJobsites,
                addToAllExistingJobs: internalUser.addToAllExistingJobs,
                sendInvitation: internalUser.id === 0,
                userEnabled: internalUser.userEnabled,
                roles: internalUser.roles,
                roleTreeData: internalUser.role,
                notificationPreferences: mapPreferences(),
                jobAccess: props.jobAccess,
                accountingEntityLinkingType:
                    entity.accountingEntity &&
                    getSelectedValue(entity.accountingEntity.linkingOptions),
                createNewAccountingEntityOption: entity.accountingEntity
                    ? entity.accountingEntity.createNewAccountingEntityOption
                    : LinkingAction.None,
                username: internalUser.username,
                enableConflictAlert: internalUser.enableConflictAlert,
                conflictAlertMaxCount: internalUser.conflictAlertMaxCount,
                scheduleItems: internalUser.calendarFeedOptions
                    ? getSelectedValues(internalUser.calendarFeedOptions.scheduleFeedOptions)
                    : [],
                scheduleItemsFeed:
                    internalUser.calendarFeedOptions?.calendarFeedItems.find(
                        (x) => x.feedType === FeedType.ScheduleItems,
                    ) ?? undefined,
                leadActivities: internalUser.calendarFeedOptions
                    ? getSelectedValues(internalUser.calendarFeedOptions.leadActivitiesFeedOptions)
                    : [],
                leadActivitesFeed:
                    internalUser.calendarFeedOptions?.calendarFeedItems.find(
                        (x) => x.feedType === FeedType.LeadActivities,
                    ) ?? undefined,
                todos: internalUser.calendarFeedOptions
                    ? getSelectedValues(internalUser.calendarFeedOptions.todoFeedOptions)
                    : [],
                todosFeed:
                    internalUser.calendarFeedOptions?.calendarFeedItems.find(
                        (x) => x.feedType === FeedType.Todos,
                    ) ?? undefined,
                geofencing: internalUser.geofencingEnabled,
                defaultCostCodeId:
                    internalUser.defaultCostCodeId ??
                    Number(
                        internalUser.defaultCostCodes.find(
                            (cc) => cc.title === "Buildertrend Flat Rate",
                        )!.id,
                    ),
                defaultLaborCost: internalUser.defaultLaborCost,
                defaultBillRate: internalUser.defaultBillRate,
                messageSignature: internalUser.messageSignature,
                messageSignatureImage: internalUser.messageSignatureImage,
                signatureImage: internalUser.signatureImage,
                contactInfo: internalUser.contactInfo,
                customFields: PropsToValueMapping(internalUser),
                payrollEmployeeId: entity.payrollSettingsEntity?.payrollEmployeeId,
                payrollJobID: entity.payrollSettingsEntity?.payrollJobID,
                defaultPayrollItem: entity.payrollItems
                    ? getSelectedValue(entity.payrollItems.treeData)
                    : null,
                tempFile:
                    internalUser.signatureImage !== blankSignatureImage
                        ? new BTServiceFileUpload({
                              fileName: "",
                              url: internalUser.signatureImage,
                              tempId: 0,
                              hasChanged: false,
                          })
                        : undefined,
                condensedEmail: undefined,
                isOutOfOffice: internalUser.isOutOfOffice,
                phoneCell: getCellPhoneFieldValue(
                    internalUser.cellPhoneNumber,
                    internalUser.countryCode,
                    entity.canEdit,
                ),
                smsOptInStatus: internalUser.smsOptInStatus,
                verificationRequest: VerificationMessageType.None,
            };
        },

        // use the yup validation object we specified above
        validationSchema: (props: IInternalUserProps) => InternalUserValidators(props),
        validateOnChange: true,
        validateOnBlur: true,
        enableReinitialize: true,
        ignoreDirtyValues: ["jobAccess"],
        handleSubmit: async () => {},
    },
)(InternalUserInternal);
